/* eslint-disable react/no-unescaped-entities */
import '../IncrementalityPlaybook.scss';
import { Img } from '@Components/Img';
import {
  INC_CDN,
  IncPlaybookSectionBox,
  navigateToSection,
  PAGE_PREFIX
} from '../IncrementalityPlaybookShared';
import { methodHeaders } from './Shopping';
import { playbookTable } from '../PlaybookTable';
import { testHeaders } from './Amazon';

const PAGE_TITLE = 'Meta';

export const Meta = () => (
  <div className='inc-playbook-page'>
    <IncPlaybookSectionBox
      content={
        <div className='basic-section center'>
          <div className='basic-text'>
            Meta is the parent company for a suite of social media platforms made up of Facebook and
            Instagram. With the two platforms carrying such a high daily active user count the
            audience pool is large making incrementality testing a very viable option for the
            majority of clients.
          </div>
          <br />
          <Img
            src={`${INC_CDN}/Meta_IncrementalConversions.png`}
            style={{ height: 500, width: 898 }}
          />
        </div>
      }
      pageTitle={PAGE_TITLE}
      title='Overview'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          {[
            'Incrementality Testing Approaches',
            'Considerations',
            'Outputs',
            'Signal Enrichment'
          ].map(section => {
            const slug = section.replace(/ /g, '-').toLowerCase();
            return (
              <a key={slug} href={`#${slug}`} onClick={() => navigateToSection(slug)}>
                {section}
              </a>
            );
          })}
        </div>
      }
      pageTitle={PAGE_TITLE}
      title='Contents'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          {playbookTable(
            [
              {
                Method: 'Conversion Lift in Platform',
                'Method Type': [
                  {
                    img: 'Icon_ExperimentMode_PlatformNative.png',
                    label: 'Platform Native'
                  }
                ],
                Description: (
                  <ul className='basic-list'>
                    <li>The Conversion Lift from Meta is a user-segmented test.</li>
                    <li>Meta randomizes the audience into treatment and control groups.</li>
                    <li>
                      Advertisements get delivered to the treatment group, and the control group
                      does not see any of the brand's ads.
                    </li>
                    <li>
                      The difference in conversions between the treatment and control shows the
                      incremental effect of advertising.
                    </li>
                    <li>
                      To kick off the conversion lift test reach out to your Meta rep and they will
                      help get the process started.
                    </li>
                  </ul>
                ),
                Pros: (
                  <ul className='basic-list'>
                    <li>Managed service reduces the lift on internal teams to set up analysis.</li>
                    <li>Available across Facebook, Instagram and Audience Network.</li>
                    <li>
                      Multi-cell tests are available to compare incrementality across different ads
                      in one test.
                    </li>
                  </ul>
                ),
                Cons: (
                  <ul className='basic-list'>
                    <li>
                      Less granular results can make it difficult to independently validate the
                      experiment.
                    </li>
                  </ul>
                )
              },
              {
                Method:
                  'Bliss Point Meta Conversion Lift Study Tool (User Based Conversion Lift Study via Meta API)',
                'Method Type': [
                  {
                    img: 'Icon_ExperimentMode_PlatformNative.png',
                    label: 'Platform Native'
                  }
                ],
                Description: (
                  <ul className='basic-list'>
                    <li>
                      The Bliss Point Meta Conversion Lift Study Tool is a guided, self-serve tool
                      for creating conversion lift studies via Meta's API directly within the Bliss
                      Point app.
                    </li>
                    <li>
                      It includes a step-by-step guide within the page and a power calculator to
                      help with test configuration.
                    </li>
                    <li>
                      The Conversion Lift from Meta is a user-segmented test that randomizes the
                      audience into treatment and control groups. Advertisements get delivered to
                      the treatment group, and the control group does not see any Meta ads. The
                      difference in conversions between the treatment and control shows the
                      incremental effect of advertising.
                    </li>
                    <li>
                      The tool includes a <strong>power calculator</strong> and{' '}
                      <strong>testing guardrails</strong> that confirm whether a client's campaign
                      is appropriately powered before a test launches, reducing the risk of
                      inconclusive results.
                    </li>
                  </ul>
                ),
                Pros: (
                  <ul className='basic-list'>
                    <li>Allows you to launch robust test from the Blisspoint app</li>
                    <li>
                      Built-in power calculator and guardrails prevent launching underpowered or
                      poorly designed tests.
                    </li>
                    <li>
                      No engineering lift required, study creation is handled in the Bliss Point app
                      UI, while monitoring and results retrieval happen in the Meta Ads Manager UI,
                      specifically the Experiments page (All Tools → Analyze and Report →
                      Experiments).
                    </li>
                    <li>
                      Results via the api have some more details than the UI. However those can also
                      be pulled for Meta-managed studies.
                    </li>
                    <li>
                      Standardized workflow across client teams, reducing setup variability and
                      human error.
                    </li>
                  </ul>
                ),
                Cons: (
                  <ul className='basic-list'>
                    <li>
                      Currently scoped to standard Meta Conversion Lift study designs; highly custom
                      configurations may still require direct API work.
                    </li>
                    <li>
                      Requires client campaigns to meet minimum spend and reach thresholds to pass
                      the feasibility check.
                    </li>
                    <li>
                      The built-in power calculator relies on historical performance data, so it
                      requires existing spend on the selected campaign(s)/ad set(s) to generate an
                      estimate. Net-new campaigns or new clients without spend history will need to
                      use{' '}
                      <a href={`${PAGE_PREFIX}/UserLevelCalculator`}>
                        our manual power estimate instead.
                      </a>
                    </li>
                  </ul>
                )
              },
              {
                Method: 'Meta GeoLift',
                'Method Type': [
                  {
                    img: 'Icon_ServiceModel_ManagedService.png',
                    label: 'Managed Service'
                  }
                ],
                Description: (
                  <ul className='basic-list'>
                    <li>
                      <a href='https://facebookincubator.github.io/GeoLift/'>GeoLift</a> from Meta
                      is an open-source geo-segmented incrementality approach.
                    </li>
                    <li>
                      Using the power analysis, duration, number of test markets, investment, and
                      power of the test is determined through multiple iterations of random sampling
                      to determine the best combination.
                    </li>
                    <li>
                      Ads are then withheld in the Geos selected as control markets, with the test
                      markets using the average investment from the power analysis as their budget.
                    </li>
                    <li>
                      In the analysis, the difference between the test and control counterfactual is
                      the incremental result from advertising.
                    </li>
                  </ul>
                ),
                Pros: (
                  <ul className='basic-list'>
                    <li>Can be used to analyze any platform that has geo-level data.</li>
                    <li>
                      Gives incremental impact on the full business and any measurable outcome/KPI.
                    </li>
                  </ul>
                ),
                Cons: (
                  <ul className='basic-list'>
                    <li>
                      The test is an open-source R script. Consultation with the Marketing
                      Science/Econometrics team is needed.
                    </li>
                    <li>Needs daily geo-segmented data and ad targeting.</li>
                    <li>Loss of revenue from turning off ads in Geos.</li>
                  </ul>
                )
              }
            ],
            methodHeaders,
            500
          )}
        </div>
      }
      id='incrementality-testing-approaches'
      pageTitle={PAGE_TITLE}
      title='Incrementality Testing Approaches'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          {playbookTable(
            [
              {
                Element:
                  'Bliss Point Meta Conversion Lift Study Tool (User Based Conversion Lift Study via Meta API)',
                Details: (
                  <div>
                    <div className='basic-header'>Set-Up</div>
                    <ol className='basic-list'>
                      <li>
                        First,{' '}
                        <a href='https://tinuiti.atlassian.net/wiki/spaces/pthd/embed/3846340613'>
                          submit a ticket here
                        </a>{' '}
                        to get the tool configured for your client.
                      </li>
                      <li>
                        You will need to include your client's <strong>Meta Account ID</strong> and{' '}
                        <strong>Pixel ID</strong> in the ticket.
                      </li>
                    </ol>
                    <br />
                    <div className='basic-header'>Accessing the Tool</div>
                    <ol className='basic-list'>
                      <li>
                        Once configured, you should be able to find the tool in the sidebar of the
                        Bliss Point app for your client.
                      </li>
                      <li>
                        Click the three dots next to <strong>Social</strong> to see the internal
                        planning tools that are available for your client, including the Meta Lift
                        Study Tool.
                      </li>
                    </ol>
                    <br />
                    <div className='basic-header'>End-to-End Study Creation Process</div>
                    <ol className='basic-list'>
                      <li>
                        <strong>Configure the study</strong> — Define the study parameters: select
                        the campaign(s) or ad set(s) to include, choose the conversion event(s) to
                        measure, and set the test duration window. The tool will prompt you through
                        each configuration step.
                      </li>
                      <li>
                        <strong>Run the power check</strong> — Before launch, the built-in power
                        calculator fetches historical performance data and computes a minimum
                        required budget for the test to be 90% powered. If your configuration budget
                        exceeds that threshold, you're ready to hit "Review and Create Test." If the
                        test is underpowered, iterate on your design. Increasing test duration is
                        the easiest lever if you have nearly enough spend to be powered. If the test
                        is not close to powered, we recommend that you rethink the test structure.
                        If your planned future budget during the test window will meet or exceed the
                        minimum required budget, you can still push the test live.
                      </li>
                      <li>
                        <strong>Launch the study</strong> — Once the power check passes, launch the
                        study directly from the UI. The tool handles study creation via the Meta
                        Conversion Lift API, no engineering lift or direct API work is required.
                      </li>
                      <li>
                        <strong>Monitor the study &amp; pull results</strong> — After publishing
                        your test, track study status and progress within Meta Ads Manager. Head to
                        the <strong>Experiments page in Ads Manager</strong> to confirm it is there
                        and set up correctly.{' '}
                        <strong>
                          This page is found in the sidebar in Ads Manager, under All Tools →
                          Analyze and Report → Experiments.
                        </strong>{' '}
                        Once the test goes live, this is also where you will be able to monitor the
                        test status and see results as they come in.
                      </li>
                    </ol>
                    <br />
                    <div className='basic-header'>Training &amp; Resources</div>
                    <ul className='basic-list'>
                      <li>
                        <a href='https://docs.google.com/presentation/d/1VxIDuCkq2xyXdoaTIcDX4CDLXLSFfGVF2aSyLnGui18/edit?usp=drive_link'>
                          Sales 1-Pager
                        </a>{' '}
                        — Overview deck for client-facing conversations
                      </li>
                      <li>
                        <a href='https://docs.google.com/presentation/d/1N7fG7sPWaujyFNDh-M9ifovsBn4qRdBqdvEhPEGfuW0/edit?slide=id.g2b81405a168_0_1287'>
                          Meta CLS Tool Guide (Walkthrough Deck)
                        </a>{' '}
                        — Step-by-step walkthrough of the tool interface
                      </li>
                      <li>
                        <a href='https://drive.google.com/file/d/1G46k74gZxKxBaGb8ttTD0j8ieclfIHQn/view?usp=drive_link'>
                          Video Walkthrough 1
                        </a>{' '}
                        — Recorded demo of the tool setup and launch process
                      </li>
                      <li>
                        <a href='https://drive.google.com/file/d/1mUd5JAmHmH-0ARpouGcQQXwa7MmN5j-C/view?usp=drive_link'>
                          Video Walkthrough 2
                        </a>{' '}
                        — Additional recorded demo (also available in-app)
                      </li>
                      <li>
                        <a href='https://drive.google.com/drive/folders/1lPfyVUHHSRuqdiBeQh23Y6lREhBNapai?usp=drive_link'>
                          Bliss Point CLS Training Folder
                        </a>{' '}
                        — Full collection of training materials, case studies, and supplemental
                        resources
                      </li>
                    </ul>
                  </div>
                )
              },
              {
                Element: 'Available Outcomes to Measure',
                Details: (
                  <ul className='basic-list'>
                    <li>Facebook Pixel for online actions</li>
                    <li>Facebook SDK for in-app events</li>
                    <li>Facebook Offline Events Manager for offline actions</li>
                  </ul>
                )
              },
              {
                Element: 'Geo-targeting Availability',
                Details: (
                  <ul className='basic-list'>
                    <li>
                      When using the Meta GeoLift it is necessary to target and run ads at the geo
                      level.
                    </li>
                    <li>
                      If geo-targeting is not a viable option, using the conversion lift test is the
                      recommended option.
                    </li>
                  </ul>
                )
              },
              {
                Element: 'Conversion API',
                Details: (
                  <ul className='basic-list'>
                    <li>
                      In order to run conversion lift it is necessary to have the Meta Conversion
                      API activated.
                    </li>
                  </ul>
                )
              },
              {
                Element: 'Lift Test vs A/B Test',
                Details: (
                  <ul className='basic-list'>
                    <li>
                      A lift test implements a holdout group to compare conversions for users who
                      see ads vs those who don’t get exposed
                    </li>
                    <li>
                      An A/B test on Meta does not include a holdout group. It compares the
                      performance of the two different campaigns.
                    </li>
                  </ul>
                )
              },
              {
                Element: 'Competing Ads',
                Details: (
                  <ul className='basic-list'>
                    <li>
                      If users in the control group are exposed to other ads from the advertiser,
                      the results of the test may be skewed.
                    </li>
                    <li>
                      Meta can help ensure non-overlapping audiences between the test audience and
                      the audiences in other normal marketing initiatives.
                    </li>
                  </ul>
                )
              }
            ],
            testHeaders,
            100
          )}
          <br />
          <br />
          <p>
            Here is the Meta's Conversion lift methodology and{' '}
            <a
              href='https://drive.google.com/file/d/1pmCGt1ZA3O7VSmKvarTrF6EgriSo1vCk/view?usp=sharing'
              target='_blank'
              rel='noreferrer'
            >
              best practices.
            </a>
          </p>
        </div>
      }
      id='considerations'
      pageTitle={PAGE_TITLE}
      title='Considerations'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          <div className='basic-text'>
            Results are available in the Facebook Ads Manager experiments UI (standard offering), as
            well as{' '}
            <a href='https://developers.facebook.com/docs/marketing-api/guides/lift-studies#'>
              through the meta API
            </a>
            . API results are more granular. For a multi-cell test, the winning cell can be
            determined by cost per conversion lift or ROAS lift. Cost per conversion lift is a
            measure of how expensive incremental conversions were in each cell. ROAS lift compares
            the test cells based on the return on ad spend to determine a winning cell.
          </div>
          <br />
          <div className='basic-header'>Single Cell Results</div>
          <Img src={`${INC_CDN}/Meta_SingleCellResults.png`} />
          <br />
          <div className='basic-header'>Multi-Cell Results</div>
          <Img src={`${INC_CDN}/Meta_MultiCellResults.png`} />
        </div>
      }
      pageTitle={PAGE_TITLE}
      title='Test Outputs'
      id='outputs'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          <div className='basic-text'>
            For all Meta incrementality products that rely on a user-based protocol – thus excluding
            geo experiments – it is strongly recommended that{' '}
            <a href='https://developers.facebook.com/docs/marketing-api/conversions-api/'>
              Conversions API (CAPI)
            </a>{' '}
            be implemented in order to strengthen conversion modeling and increase the overall
            fidelity of measurement. This is especially important for tests of lower-funnel
            outcomes.
          </div>
          <br />
          <div className='basic-text'>
            CAPI is designed to create a direct and more reliable connection between marketing data
            (from a brand’s server, website platform, app or CRM) and Meta. Examples of marketing
            data include{' '}
            <a href='https://www.facebook.com/business/help/964258670337005?id=1205376682832142'>
              website events
            </a>
            ,{' '}
            <a href='https://www.facebook.com/business/help/235457266642587?id=1716638325041491'>
              app events
            </a>{' '}
            and{' '}
            <a href='https://www.facebook.com/business/help/1142103235885551?id=565900110447546'>
              offline conversions
            </a>
            . This marketing data helps enable ad personalization, optimization and measurement
            across Meta technologies so that ads are shown to{' '}
            <a href='https://www.facebook.com/business/help/283579896000936'>people</a> who are more
            likely to find them relevant.
          </div>
          <br />
          <div className='basic-text'>
            If a client does not already have CAPI implemented, we can help them via Tinuiti’s{' '}
            <a href='https://www.facebook.com/business/help/746511563926080?id=818859032317965'>
              CAPI GMA
            </a>{' '}
            infrastructure.
          </div>
        </div>
      }
      id='signal-enrichment'
      pageTitle={PAGE_TITLE}
      title='Signal Enrichment'
    />
  </div>
);
